import React, { createContext, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { Chat, Remirror as RemirrorTypes } from '@trinity/chat';
import { ChatCitationType } from '@trinity/chat';
import type { ResearchQuoteWidgetData } from '@trinity/chat/types/dashboard-data';
import { SystemMessageContainer } from '../../Chat/Message.System.Container.js';
import { MessageCitations } from '../../Chat.Message.Citations/index.js';
import { ResponsePane } from '../../ProjectTags/PatientJourneyDashboard/ResponsePane.js';
import { useRemirrorEditor } from '../../Remirror/BasicMarkupEditor.js';
import { WidgetQuoteCardCompact } from '../../Widget.Quote/Widget.Quote.Card.Compact.js';

type QuoteSource = ResearchQuoteWidgetData['source'];

/** Resolves a quote's transcript source to a link; null when the host has no transcript page for it. */
export type QuoteSourceLinkResolver = (source: QuoteSource) => string | null;

const QuoteSourceLinkContext = createContext<QuoteSourceLinkResolver | null>(null);

/** Lets a host (PJ, segments, …) make quote sources clickable. Without it, sources show unlinked. */
export function QuoteSourceLinkProvider({
  resolve,
  children,
}: {
  resolve: QuoteSourceLinkResolver;
  children: React.ReactNode;
}): React.ReactElement {
  return <QuoteSourceLinkContext.Provider value={resolve}>{children}</QuoteSourceLinkContext.Provider>;
}

/** A research quote: the compact quote card with its media clip, plus a collapsible Sources pane. */
export function ResearchQuote({ data }: { data: ResearchQuoteWidgetData }): React.ReactElement {
  const resolveLink = useContext(QuoteSourceLinkContext);
  const [open, setOpen] = useState(false);
  const { state } = useRemirrorEditor({ content: data.quote as RemirrorTypes.RootNode });
  const link = resolveLink?.(data.source) ?? null;

  const sources = useMemo(
    () =>
      [
        {
          ordinal: 1,
          link,
          type: ChatCitationType.Transcript,
          score: null,
          title: `Transcript (${data.source.transcriptId})`,
          snippets: [
            {
              ordinal: 1,
              score: null,
              title: null,
              text: `#### ${data.speaker?.name ?? ''}\n${state.doc.textContent}`,
              metadata: {
                start: data.speaker?.start ?? 0,
                end: data.speaker?.end ?? 0,
                project_id: data.source.projectId,
                call_id: data.source.callId,
              },
              isMarkdown: true,
            },
          ],
        },
      ] as unknown as Chat.Citation[],
    [link, data, state],
  );

  return (
    <>
      <WidgetQuoteCardCompact
        quote={data.quote as RemirrorTypes.RootNode}
        insights={data.insights as RemirrorTypes.RootNode}
        media={data.media as never}
      />
      <SourceRoot>
        <SystemMessageContainer citations={sources}>
          <ResponsePane title={`Sources (${sources.length})`} open={open} onToggle={() => setOpen((v) => !v)}>
            <MessageCitations items={sources} />
          </ResponsePane>
        </SystemMessageContainer>
      </SourceRoot>
    </>
  );
}

const SourceRoot = styled.div({
  borderTop: '1px solid rgba(0,0,0,0.08)',
  flexShrink: 0,
});
